import { Helmet } from 'react-helmet-async';
import { filter } from 'lodash';
import { useEffect, useState } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';

// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Avatar,
  Popover,
  Checkbox,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  CircularProgress,
  Box,
} from '@mui/material';

import { toast } from 'react-toastify';

// components
import Iconify from '../iconify';
import Scrollbar from '../scrollbar';

// sections
import { UserListHead } from '../../sections/@dashboard/user';

// mock
// import { users as USERLIST } from '../../_mock/user';
import useUsers from '../../hooks/useUsers';

const TABLE_HEAD = [
  { id: 'name', label: 'Name', alignRight: false },
  { id: 'gener', label: 'Gender', alignRight: false },
  { id: 'memberType', label: 'Member type', alignRight: false },
  { id: 'birthday', label: 'Birthday', alignRight: false },
  { id: 'department', label: 'Department', alignRight: false },
  { id: '' },
];

// ----------------------------------------------------------------------



function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(array, (_user) => _user.name.toLowerCase().indexOf(query.toLowerCase()) !== -1);
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function Members() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(null);

  const [page, setPage] = useState(0);

  const [order, setOrder] = useState('asc');

  const [selected, setSelected] = useState([]);

  const [orderBy, setOrderBy] = useState('name');

  const [filterName, setFilterName] = useState('');

  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [openDialog, setOpenDialog] = useState(false);

  const [selectedUserId, setSelectedUserId] = useState(null);

  const {
    setUsers,
    USERLIST,
    loader,
    error,
  } = useUsers();

  const handleOpenMenu = (event) => {
    setOpen(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpen(null);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = USERLIST.map((n) => n.memberId);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, memberId) => {
    const selectedIndex = selected.indexOf(memberId);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, memberId);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const { memberId: excludedMemberId } = useParams();


  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - USERLIST.length) : 0;

  const filteredUserList = USERLIST.filter(user => user.memberId !== excludedMemberId);

  const filteredUsers = applySortFilter(filteredUserList, getComparator(order, orderBy), filterName);


  const isNotFound = !filteredUsers.length && !!filterName;

  const showHeadCheckBox = true;

  const handleViewUser = async (event) => {
    if (!selectedUserId) {
      toast('no selected user ');
      return; // No user selected, return early
    }
    navigate(`/dashboard/members/${selectedUserId}/memberview`);
    handleCloseMenu(event)
  };

  function formatDateOfBirth(inputDate) {
    // Parse the input date
    const date = new Date(inputDate);

    // Check if the date is valid
    if (Number.isNaN(date.getTime())) {
      return 'Invalid Date';
    }

    // Get the abbreviated month name
    const monthName = date.toLocaleString('default', { month: 'short' });

    // Format the month name with a period
    const formattedMonth = `${monthName}.`;

    // Get the day component
    const formattedDay = date.getDate();

    // Concatenate the formatted components into the desired format
    const formattedDate = `${formattedMonth} ${formattedDay}`;

    return formattedDate;
  }

  return (
    <>
      <Card style={{ background: '#fcfcfc' }}>
        <Typography fontSize={18} fontWeight={600} color="#808191" padding={3}>
          Other Members
        </Typography>
        {loader ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress sx={{ color: '#000080' }} />
          </Box>
        ) : (
          <>
            <Scrollbar>
              <TableContainer sx={{ minWidth: 800 }} style={{ background: '#fcfcfc' }}>
                <Table>
                  <UserListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={USERLIST.length}
                    numSelected={selected.length}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={handleSelectAllClick}
                    showHeadCheckBox={showHeadCheckBox}
                  />
                  <TableBody>
                    {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                      const { memberId, firstName, lastName, memberType, gender, avatarUrl, birthday, department, phoneNumber } = row;
                      const selectedUser = selected.indexOf(memberId) !== -1;

                      return (
                        <TableRow hover key={memberId} tabIndex={-1} role="checkbox" selected={selectedUser}>
                          <TableCell padding="checkbox">
                            <Checkbox checked={selectedUser} onChange={(event) => handleClick(event, memberId)} />
                          </TableCell>

                          <TableCell component="th" scope="row" padding="none">
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Avatar alt={firstName} src="./imjj/plo.png" />

                              <div className="wrap">
                                <div className="name">
                                  <Typography variant="subtitle2" noWrap>
                                    {`${firstName} ${lastName}`}
                                  </Typography>
                                </div>

                                <div className="phone">
                                  <Typography variant="subtitle6" noWrap>
                                    {phoneNumber}
                                  </Typography>
                                </div>
                              </div>
                            </Stack>
                          </TableCell>

                          <TableCell align="left">{gender}</TableCell>

                          <TableCell align="left">{memberType}</TableCell>

                          <TableCell align="left">{`${formatDateOfBirth(birthday)}`}</TableCell>

                          <TableCell align="left">{department}</TableCell>

                          <TableCell align="right">
                            <IconButton
                              size="large"
                              color="inherit"
                              onClick={(event) => {
                                setSelectedUserId(memberId);
                                handleOpenMenu(event);
                              }}
                            >
                              <Iconify icon={'eva:more-vertical-fill'} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                  </TableBody>
                  {error ? (
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                          <Paper
                            sx={{
                              textAlign: 'center',
                            }}
                          >
                            <Typography variant="h6" paragraph color={'#585858'}>
                              Message:
                            </Typography>

                            <Typography variant="body2" color={'#808191'}>
                              {error}
                            </Typography>
                          </Paper>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  ) : (
                    isNotFound && (
                      <TableBody>
                        <TableRow>
                          <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                            <Paper
                              sx={{
                                textAlign: 'center',
                              }}
                            >
                              <Typography variant="h6" paragraph>
                                Not found
                              </Typography>

                              <Typography variant="body2">
                                No results found for &nbsp;
                                <strong>&quot;{filterName}&quot;</strong>.
                                <br /> Try checking for typos or using complete words.
                              </Typography>
                            </Paper>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    )
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={USERLIST.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Card>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        <Link
          to={`/dashboard/members/${selectedUserId}/memberview`}
          style={{ textDecoration: 'none', color: '#212B36' }}
        >
          <MenuItem>
            <Iconify icon={'carbon:view-filled'} sx={{ mr: 2 }} onClick={handleViewUser} />
            View
          </MenuItem>
        </Link>

        {/* <MenuItem sx={{ color: 'error.main' }}>
          <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />
          Delete
        </MenuItem> */}
      </Popover>
    </>
  );
}
